import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';

interface DoorSensorCardProps {
  deviceId: string;
  deviceName: string;
  lastData?: any;
  config?: any;
}

export const DoorSensorCard: React.FC<DoorSensorCardProps> = ({ deviceName, lastData, config }) => {
  const { t } = useTranslation();
  const doorState = lastData?.door_state || 'unknown';
  const batteryLevel = lastData?.battery_v || 0;
  const isOpen = doorState === 'open' || doorState === '1' || doorState === true;

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Ionicons name="lock-open-outline" size={24} color="#f59e0b" />
        <Text style={styles.deviceName}>{deviceName}</Text>
      </View>

      {/* Door Status */}
      <View style={styles.statusContainer}>
        <View style={[styles.statusBadge, isOpen ? styles.openBadge : styles.closedBadge]}>
          <Ionicons
            name={isOpen ? 'lock-open-outline' : 'lock-closed-outline'}
            size={32}
            color="#fff"
          />
          <Text style={styles.statusText}>
            {isOpen ? t('deviceDetail.doorOpen') : t('deviceDetail.doorClosed')}
          </Text>
        </View>
      </View>

      {/* Battery Level */}
      {batteryLevel > 0 && (
        <View style={styles.metricRow}>
          <Ionicons name="battery-half" size={20} color="#64748b" />
          <Text style={styles.metricLabel}>{t('deviceDetail.battery')}</Text>
          <Text style={styles.metricValue}>{batteryLevel.toFixed(2)} V</Text>
        </View>
      )}

      {/* Last Update */}
      {lastData?.timestamp && (
        <Text style={styles.timestamp}>
          {t('deviceDetail.lastUpdate')}: {new Date(lastData.timestamp).toLocaleString()}
        </Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  deviceName: {
    fontSize: 18,
    fontWeight: '600',
    marginLeft: 12,
    color: '#1f2937',
  },
  statusContainer: {
    alignItems: 'center',
    marginVertical: 24,
  },
  statusBadge: {
    paddingHorizontal: 32,
    paddingVertical: 20,
    borderRadius: 16,
    alignItems: 'center',
    minWidth: 200,
  },
  openBadge: {
    backgroundColor: '#ef4444',
  },
  closedBadge: {
    backgroundColor: '#10b981',
  },
  statusText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '700',
    marginTop: 8,
  },
  metricRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    marginTop: 8,
  },
  metricLabel: {
    fontSize: 14,
    color: '#6b7280',
    marginLeft: 8,
    flex: 1,
  },
  metricValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1f2937',
  },
  timestamp: {
    fontSize: 12,
    color: '#9ca3af',
    textAlign: 'center',
    marginTop: 12,
  },
});
